import { State } from "../../../../redux/slices";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Chip, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { getLocalizedTextByLocaleVersionTwo } from "../../../../utils/localized-text";
import { AssociatedGeoPoint } from "../../../../redux/actions/users/userTypes";
import { ColumnTables } from "../../../../redux/slices/userGroups/userTablestyping";
import { GridColDef } from "@mui/x-data-grid-pro";
import { selectUsersAndLicences } from "../../../../redux/slices/users/users";

import { selectLocale } from "../../../../redux/slices/layout";

import {
  applyLocalFilter,
  getOperatorsFromAvailableFilters,
} from "../../../helpers/helpersSearchs";
import CustomGeneralColumnHeader from "../../../customFilterComponent/customHeader";
import { ThunkDispatch } from "@reduxjs/toolkit";
import {
  setPreselectedFilterField,
  setShowFilterForm,
} from "../../../../redux/slices/userGroups/userGroupsSliceMultipleTables";

const SetActions = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();

  return {
    onOpenFilter: (field: string) => {
      dispatch(setShowFilterForm(true));
      dispatch(setPreselectedFilterField(field));
    },
  };
};

const CreateAllUsersColumns = () => {
  const [t] = useTranslation("global");
  const {
    allUserColumnsTable,
    usersSBFilters,
    searchInputUsersTable,
    filterModel,
  } = useSelector((state: State) => state.CreateTablesUsersReducer);

  const users = useSelector(selectUsersAndLicences);
  const locale = useSelector(selectLocale);

  const { onOpenFilter } = SetActions();

  const columnNames = {
    id: t("Data.table.Details"),
    username: t("Users_tables.columns_table_one.users"),
    role: t("Users_tables.columns_table_one.role"),
    assignedSite: t("Users_tables.columns_table_one.sites"),
    balance: t("Users_tables.columns_table_one.balance"),
    assignedAction: t("Users_tables.columns_table_one.actions"),
    varieties: t("Users_tables.columns_table_one.varieties"),
    actions: "",
  };

  const actionFilterItem = filterModel?.items?.find(
    (item) => item.field === "assignedAction" && item.operator === "$in",
  );

  const actionsSelectedFilter = actionFilterItem?.value
    ? Array.isArray(actionFilterItem.value)
      ? actionFilterItem.value
      : []
    : usersSBFilters.actions.valuesSelected.map((values) => values.id);

  const resultsToSearch = users.filter((user) => {
    if (!user) {
      return false;
    }
    const normalizeUsername = user.username
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "");
    const normalizeEmail = user.email
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "");

    // Apply all LOCAL filters from filterModel
    if (filterModel?.items && filterModel.items.length > 0) {
      const passesAllFilters = filterModel.items.every((filterItem) => {
        // Handle assignedAction filter
        if (
          filterItem.field === "assignedAction" &&
          filterItem.operator === "$in"
        ) {
          const userActionIds = (user as any).actionsIds || [];
          const filterValues = Array.isArray(filterItem.value)
            ? filterItem.value
            : [];
          if (filterValues.length === 0) return true;
          return userActionIds.some((actionId: number) =>
            filterValues.includes(actionId),
          );
        }

        if (
          filterItem.service === "operator" ||
          filterItem.service === "text"
        ) {
          return applyLocalFilter(filterItem, user);
        }

        return true;
      });

      // Also check search input
      const matchesSearch =
        searchInputUsersTable === "" ||
        normalizeUsername
          ?.toLowerCase()
          .includes(searchInputUsersTable.toLowerCase()) ||
        normalizeEmail
          ?.toLowerCase()
          .includes(searchInputUsersTable.toLowerCase());

      return passesAllFilters && matchesSearch;
    }

    // Fallback to old filter logic if no filterModel
    if (actionsSelectedFilter.length === 0 && searchInputUsersTable === "") {
      return user;
    } else if (
      user.actionsIds.find(
        (x) =>
          actionsSelectedFilter.find((e) => e === x) &&
          searchInputUsersTable === "",
      )
    ) {
      return user;
    } else if (
      user.actionsIds.find(
        (x) =>
          actionsSelectedFilter.find((e) => e === x) &&
          normalizeUsername
            ?.toLowerCase()
            .includes(searchInputUsersTable.toLowerCase()),
      )
    ) {
      return user;
    } else if (
      actionsSelectedFilter.length === 0 &&
      normalizeUsername
        ?.toLowerCase()
        .includes(searchInputUsersTable.toLowerCase())
    ) {
      return user;
    } else if (
      user.actionsIds.find(
        (x) =>
          actionsSelectedFilter.find((e) => e === x) &&
          normalizeEmail
            ?.toLowerCase()
            .includes(searchInputUsersTable.toLowerCase()),
      )
    ) {
      return user;
    } else if (
      actionsSelectedFilter.length === 0 &&
      normalizeEmail
        ?.toLowerCase()
        .includes(searchInputUsersTable.toLowerCase())
    ) {
      return user;
    }

    return "";
  });

  let allUsersColumns: any[] = allUserColumnsTable.map(
    (column: ColumnTables) => {
      if (column.field === "username") {
        return {
          field: column.field,
          headerName: (columnNames as never)[column.field],
          width: 220,
          alwaysHide: column.alwaysHide,
          disableColumnMenu: true,
          sortable: false,
          type: "string",
          filterOperators: getOperatorsFromAvailableFilters([]),
          filter: "LOCAL",
          service: "",
          queryFormat: "",

          renderCell: (params: any) => {
            return (
              <div className="username-managing-styles-column">
                <div className="username-title-columns">
                  {params === null || params === undefined ? "" : params.value}
                </div>
                <div className="mail-title-columns">
                  {params.row.email === null || params.row.email === undefined
                    ? ""
                    : params.row.email}
                </div>
              </div>
            );
          },
        };
      }

      if (column.field === "assignedAction") {
        return {
          field: column.field,
          headerName: (columnNames as never)[column.field],
          minWidth: 230,
          alwaysHide: column.alwaysHide,
          disableColumnMenu: true,
          sortable: false,
          type: "string",
          filterOperators: getOperatorsFromAvailableFilters(["$in"]),
          filter: "LOCAL",
          service: "actions",
          queryFormat: "",
          renderHeader: () => (
            <CustomGeneralColumnHeader
              headerName={(columnNames as never)[column.field]}
              field={column.field}
              filterModel={filterModel}
              availableFilters={["$in"]}
              onOpenFilter={onOpenFilter}
            />
          ),
          renderCell: (params: any) => {
            const values = Array.isArray(params?.value) ? params.value : [];
            const actionNames = values.map((x: any) => [
              getLocalizedTextByLocaleVersionTwo(locale, x.name.localizedTexts),
              getLocalizedTextByLocaleVersionTwo(
                locale,
                x.cerealType.localizedTexts,
              ),
            ]);

            if (actionNames.length === 0) return <span />;

            if (actionNames.length === 1) {
              return (
                <span>{`(${actionNames[0][1]}) ${actionNames[0][0]}`}</span>
              );
            }

            return (
              <span>
                {`(${actionNames[0][1]}) ${actionNames[0][0]} + ${actionNames.length - 1} ${t("Common_options.others")}`}
              </span>
            );
          },
        };
      }
      if (column.field === "role") {
        return {
          field: column.field,
          headerName: (columnNames as never)[column.field],
          width: 100,
          alwaysHide: column.alwaysHide,
          disableColumnMenu: true,
          sortable: false,
          type: "string",
          renderCell: (params: any) => {
            return (
              <div className="username-managing-styles-column">
                <Chip label={params.value} variant="outlined" />
              </div>
            );
          },
        };
      }

      if (column.field === "assignedSite") {
        return {
          field: column.field,
          headerName: (columnNames as never)[column.field],
          alwaysHide: column.alwaysHide,
          width: 250,
          disableColumnMenu: true,
          sortable: false,
          type: "string",
          filterOperators: getOperatorsFromAvailableFilters(["$in"]),
          filter: "API",
          service: "geoPoints",
          queryFormat: "associatedGeoPointId[$in][]=",
          // valueGetter returns a string so the quick filter can search by site name (MUI X v6 signature)
          valueGetter: (params: any) => {
            const sites: AssociatedGeoPoint[] = Array.isArray(params?.value)
              ? params.value
              : [];
            return sites
              .map((s) => s?.name)
              .filter(Boolean)
              .join(", ");
          },
          renderHeader: () => (
            <CustomGeneralColumnHeader
              headerName={(columnNames as never)[column.field]}
              field={column.field}
              filterModel={filterModel}
              availableFilters={["$in"]}
              onOpenFilter={onOpenFilter}
            />
          ),
          // renderCell reads the raw array from params.row, not params.value (which is the string from valueGetter)
          renderCell: (params: any) => {
            const sites: AssociatedGeoPoint[] = Array.isArray(
              params?.row?.assignedSite,
            )
              ? params.row.assignedSite
              : Array.isArray(params?.value)
                ? params.value
                : [];
            const names = sites.map((v) => v?.name).filter(Boolean) as string[];

            if (names.length === 0) {
              return (
                <span>{`0  ${t("Users_tables.columns_table_one.sites")}`}</span>
              );
            }
            if (names.length === 1) {
              return <span>{names[0]}</span>;
            }
            return (
              <span>{`${names[0]} + ${names.length - 1} ${t("Common_options.others")}`}</span>
            );
          },
        };
      }

      if (column.field === "actions") {
        return {
          field: column.field,
          headerName: (columnNames as never)[column.field],
          width: 100,
          alwaysHide: column.alwaysHide,
          disableColumnMenu: true,
          sortable: false,
          type: "string",
          renderCell: (params: any) => {
            return (
              <IconButton aria-label="edit">
                <EditIcon />
              </IconButton>
            );
          },
        };
      }

      return {
        field: column.field,
        headerName: (columnNames as never)[column.field],
        width: column.width,
        filterable: false,
        sortable: false,
        disableColumnMenu: true,
        alwaysHide: column.alwaysHide,
        editable: true,
      };
    },
  );

  let rowsAllUsersTable = resultsToSearch.map((row) => {
    const filter = row?.associatedGeoPoint.filter(function (
      element: AssociatedGeoPoint,
    ) {
      return element !== undefined;
    });
    const rows = {
      id: row.id,
      username: row.username,
      email: row.email,
      role: row.userType,
      assignedSite: filter,
      balance: row.licenses,
      assignedAction: (row as any).scopesIds,
      assignedSiteId:
        row?.associatedGeoPoint.length === 0
          ? []
          : filter.map((site: AssociatedGeoPoint) => site?.id),
      isNew: false,
      actions: row.id,
      createdAt: row.createdAt,
      creator: "",
      password: "",
      contactEmail: "",
      phone: "",
      groups: row.groups.length >= 1 ? row.groups : [],
      groupMemberships:
        row.groupMemberships.length > 0 ? row.groupMemberships : [],
      rawGroups: row.rawGroups,
    };
    return rows;
  }) as any;

  const rowsSorted = rowsAllUsersTable?.sort(
    (a: any, b: any) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  const allUsersColumnsFilter = allUsersColumns?.filter(
    (columns: ColumnTables) => !columns.alwaysHide,
  );

  return {
    allUsersColumns: allUsersColumnsFilter as GridColDef[],
    rowsAllUsersTable: rowsSorted,
  };
};

export default CreateAllUsersColumns;
